import { NoSsr } from "@mui/base";
import type { FunctionComponent, ReactNode } from "react";
import { useEffect, useMemo } from "react";

import { useResponsive } from "shared/hooks";
import { Edition } from "shared/lib/edition";
import { theme } from "shared/lib/styles";

import { useIsScrollingOverAppBarThreshold } from "scmp-app/components/app-bar";
import { HideElementOnScrollEffectThreshold } from "scmp-app/components/app-bar/consts";
import { useAppBarContext } from "scmp-app/components/app-bar/contexts";
import { useAppBarSetFixedHeaderHeight } from "scmp-app/components/app-bar/hooks/sticky/app-bar-set-fixed-header-height";
import { useAppBarSetStickyScrollHeight } from "scmp-app/components/app-bar/hooks/sticky/app-bar-set-sticky-scroll-height";
import { useHeaderFeaturesProvider } from "scmp-app/components/header/features/hooks";
import type { HeaderConfiguration } from "scmp-app/components/header/types";

import { DynamicHomeNewsTicker } from "./dynamics";
import { HomeEdition } from "./edition";
import {
  CenterFeatureContainer,
  Content,
  HeaderContainer,
  HomeHeaderContainer,
  LeftFeatureContainer,
  OnelineMenuContainer,
  RightFeatureContainer,
  StyledHeaderOnelineMenuContainer,
  StyledHomeMiniHeader,
} from "./styles";

export type Props = {
  className?: string;
  headerConfiguration?: HeaderConfiguration;
  initEdition?: Edition;
};

export const HomeHeader: FunctionComponent<Props> = ({
  className,
  headerConfiguration,
  initEdition,
}) => {
  const { setShouldSwap } = useAppBarContext();
  const { reference } = useAppBarSetStickyScrollHeight();

  const isDesktopUp = useResponsive(theme.breakpoints.up("desktop"));
  const threshold = isDesktopUp
    ? HideElementOnScrollEffectThreshold.desktop
    : HideElementOnScrollEffectThreshold.mobile;
  const shouldSwap = useIsScrollingOverAppBarThreshold(threshold);
  const { reference: miniHeaderReference } =
    useAppBarSetFixedHeaderHeight<HTMLDivElement>(shouldSwap);
  const shouldUseBilingual = initEdition === Edition.HongKong;

  useEffect(() => {
    if (!setShouldSwap) return;
    setShouldSwap(shouldSwap ?? false);
  }, [setShouldSwap, shouldSwap]);

  const headerFeatureProvider = useHeaderFeaturesProvider();

  const layout = useMemo(() => {
    const defaultLayout: { center: ReactNode; left: ReactNode; right: ReactNode } = {
      center: (
        <CenterFeatureContainer>
          {headerFeatureProvider.logoTextLink({
            bilingual: shouldUseBilingual,
            showIn: "desktopUp",
            size: "large",
          })}
          {headerFeatureProvider.logoTextLink({
            showIn: "mobileAndTablet",
            size: "large",
          })}
        </CenterFeatureContainer>
      ),
      left: (
        <LeftFeatureContainer>
          {headerFeatureProvider.hamburgerMenu({ showIn: "mobileOnly" })}
          {headerFeatureProvider.search({ showIn: "mobileOnly" })}
        </LeftFeatureContainer>
      ),
      right: (
        <RightFeatureContainer>
          {headerFeatureProvider.dashboard({
            showIn: "mobileOnly",
          })}
        </RightFeatureContainer>
      ),
    };

    return defaultLayout;
  }, [headerFeatureProvider, shouldUseBilingual]);

  const appBarStickyScrollHeight = headerConfiguration?.appBarStickyScrollHeight ?? 0;

  return (
    <>
      <HomeHeaderContainer className={className} ref={reference}>
        <HomeEdition />
        <HeaderContainer>
          <NoSsr>
            <DynamicHomeNewsTicker />
          </NoSsr>
          <Content>
            {layout.left}
            {layout.center}
            {layout.right}
          </Content>
          <OnelineMenuContainer>
            <StyledHeaderOnelineMenuContainer initEdition={initEdition} />
          </OnelineMenuContainer>
        </HeaderContainer>
      </HomeHeaderContainer>

      <StyledHomeMiniHeader
        $appBarStickyScrollHeight={appBarStickyScrollHeight}
        $shouldSwap={shouldSwap}
        ariaLabel="Scrolled primary"
        headerConfiguration={headerConfiguration}
        initEdition={initEdition}
        isHomeMiniHeader={true}
        ref={miniHeaderReference}
      />
    </>
  );
};

HomeHeader.displayName = "HomeHeader";
